import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  DatePicker,
  Input,
  Row,
  Col,
  Card,
  Statistic,
  Table,
  Tag,
  Popconfirm,
  Tooltip,
  message,
  Modal,
  Select,
} from "antd";
import {
  ArrowUpOutlined,
  BarChartOutlined,
  BugOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  RedoOutlined,
  EyeOutlined,
  PieChartOutlined,
  DownloadOutlined,
  SaveOutlined,
  SearchOutlined,
  StopOutlined,
  FormOutlined,
} from "@ant-design/icons";
import { updateBalance } from "../actions/auth";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isBetween from "dayjs/plugin/isBetween";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isEmpty from "../validations/is-empty";
import apiClient from "../actions/api";
import openNotification from "../components/Notifications/openNotification";
import getDateAndTime from "../untils/getDateAndTime";
import getDate from "../untils/getDate";
import useSpecialActions from "../hooks/useSpecialActions";
import { getJobDomain, getJobFaviconUrl } from "../untils/jobPlatform";

// import style
import "../style/dashboard.scss";

const { RangePicker } = DatePicker;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);

const allStatus = [
  {
    color: "gold",
    status: "Waiting",
  },
  {
    color: "",
    status: "Recruiter Phone",
  },
  {
    color: "",
    status: "Recruiter Video",
  },
  {
    color: "rgb(190, 0, 161)",
    status: "HR Call",
  },
  {
    color: "rgb(204, 0, 0)",
    status: "Technical",
  },
  {
    color: "rgb(0, 194, 204)",
    status: "Behavioural",
  },
  {
    color: "rgb(0, 157, 16)",
    status: "Final",
  },
  {
    color: "red",
    status: "Failed",
  },
];

const Dashboard = ({ title }) => {
  const canUseSpecialActions = useSpecialActions();
  const dispatch = useDispatch();
  // set title
  document.title = title;

  const dateAndTimeFormat = "YYYY-MM-DD HH:mm";
  const dateFormat = "YYYY-MM-DD";
  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const themeMode = useSelector((state) => state.themeMode);
  const timezoneMode = useSelector((state) => state.timezoneMode);

  const [, setAllData] = useState([]);
  const [searchedData, setSearchedData] = useState([]);
  const [modelUsers, setModelUsers] = useState([]);
  const [searchStatus, setSearchStatus] = useState(0);
  const [searchAllApplications, setSearchAllApplications] = useState(false);
  const [appliedSearchAllApplications, setAppliedSearchAllApplications] =
    useState(false);
  const [searchText, setSearchText] = useState("");
  const [directSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalResults, setTotalResults] = useState(0);
  const [searchStartDate, setSearchStartDate] = useState(
    dayjs(getDate(dayjs())).tz(timezoneMode.timezoneMode, true),
  );
  const [searchEndDate, setSearchEndDate] = useState(
    dayjs(getDate(dayjs())).tz(timezoneMode.timezoneMode, true),
  );
  const [loading, setLoading] = useState(false);

  // statistic state value
  const [totalBid, setTotalBid] = useState(0);
  const [inProgressBid, setInProgressBid] = useState(0);
  const [waitingBid, setWaitingBid] = useState(0);
  const [failedBid, setFailedBid] = useState(0);

  // Edit modal variable
  const [isCallerModalOpen, setIsCallerModalOpen] = useState(false);
  const [editBidID, setEditBidID] = useState("");
  const [editCallerName, setEditCallerName] = useState("");
  const [editStatusText, setEditStatusText] = useState(0);
  const [editFileName, setEditFileName] = useState("");
  const [editCompanyName, setEditCompanyName] = useState("");
  const [editMeetingLink, setEditMeetingLink] = useState("");
  const [editClientAddress, setEditClientAddress] = useState("");
  const [editStartDate, setEditStartDate] = useState(null);
  const [editEndDate, setEditEndDate] = useState(null);
  const [editModalLoading, setEditModalLoading] = useState(false);
  const [callDataFlag, setCallDataFlag] = useState(false);

  const getColorFromStatus = (status) => {
    const found = allStatus.find((item) => item.status === status);
    return found ? found.color : null;
  };

  const handleModalSave = async () => {
    if (isEmpty(editBidID)) {
      openNotification(
        "error",
        "Validation Error!",
        "Please choose correct field.",
      );
      return;
    }

    if (editStatusText !== 0 && editStatusText !== 6) {
      if (isEmpty(editCallerName)) {
        openNotification("error", "Validation Error!", "Please choose caller.");
        return;
      }

      if (isEmpty(editMeetingLink)) {
        openNotification(
          "error",
          "Validation Error!",
          "Please copy the Meeting Link.",
        );
        return;
      }

      if (isEmpty(editClientAddress)) {
        openNotification(
          "error",
          "Validation Error!",
          "Please type Client Address.",
        );
        return;
      }

      if (isEmpty(editStartDate)) {
        openNotification(
          "error",
          "Validation Error!",
          "Please select Start Time.",
        );
        return;
      }

      if (isEmpty(editEndDate)) {
        openNotification(
          "error",
          "Validation Error!",
          "Please select End Time.",
        );
        return;
      }
    }

    const params = {
      id: editBidID,
      caller: editCallerName,
      status: editStatusText,
      docx_filename: editFileName,
      company_name: editCompanyName,
      meeting_link: editMeetingLink,
      client_address: editClientAddress,
      start_date: isEmpty(editStartDate)
        ? ""
        : dayjs(editStartDate).tz(timezoneMode.timezoneMode),
      end_date: isEmpty(editEndDate)
        ? ""
        : dayjs(editEndDate).tz(timezoneMode.timezoneMode),
    };

    setEditModalLoading(true);

    await apiClient
      .post("/bids/edit-caller", params)
      .then(async (res) => {
        if (res.data?.status === "success") {
          openNotification("success", "Success", res.data.msg);
          await getDataAfterAction();
        }
        handleModalCancel();
        setEditModalLoading(false);
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
        handleModalCancel();
        setEditModalLoading(false);
      });
  };

  const handleModalCancel = () => {
    setEditBidID("");
    setEditCallerName("");
    setEditCompanyName("");
    setEditFileName("");
    setEditStatusText(0);
    setIsCallerModalOpen(false);
  };

  const handleCopyDetail = async (record) => {
    if (isEmpty(user)) {
      openNotification(
        "error",
        "Validation Error!",
        "Please refresh the page.",
      );
      return;
    }

    setLoading(true);

    const params = {
      id: record.id,
      user_id: user._id,
    };

    await apiClient
      .post("/bids/get-prompt-detail-by-bid", params)
      .then(async (res) => {
        if (res.data.status === "success") {
          handleCopy(res.data.prompt);
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
      });
  };

  const handleClickEditBtn = (record) => {
    setEditBidID(record.id);
    setEditCallerName(isEmpty(record?.caller_id) ? "" : record.caller_id);
    setEditFileName(isEmpty(record?.docx_filename) ? "" : record.docx_filename);
    setEditCompanyName(
      isEmpty(record?.company_name) ? "" : record.company_name,
    );
    setEditStatusText(
      record.status === "Waiting"
        ? 0
        : record.status === "Recruiter Phone"
          ? 1
          : record.status === "Recruiter Video"
            ? 2
            : record.status === "HR Call"
              ? 7
              : record.status === "Technical"
                ? 3
                : record.status === "Behavioural"
                  ? 4
                  : record.status === "Final"
                    ? 5
                    : 6,
    );
    setCallDataFlag(
      record.status === "Waiting"
        ? false
        : record.status === "Failed"
          ? false
          : true,
    );
    setEditMeetingLink(
      record.status === "Waiting" && record.status === "Failed"
        ? ""
        : isEmpty(record?.meeting_link)
          ? ""
          : record.meeting_link,
    );
    setEditClientAddress(
      record.status === "Waiting" && record.status === "Failed"
        ? ""
        : isEmpty(record?.client_address)
          ? ""
          : record.client_address,
    );
    setEditStartDate(
      record.status === "Waiting" && record.status === "Failed"
        ? ""
        : isEmpty(record?.start_date)
          ? ""
          : dayjs(
              dayjs(getDateAndTime(record.start_date), dateAndTimeFormat),
            ).tz(timezoneMode.timezoneMode, true),
    );
    setEditEndDate(
      record.status === "Waiting" && record.status === "Failed"
        ? ""
        : isEmpty(record?.end_date)
          ? ""
          : dayjs(dayjs(getDateAndTime(record.end_date), dateAndTimeFormat)).tz(
              timezoneMode.timezoneMode,
              true,
            ),
    );
    setIsCallerModalOpen(true);
  };

  const handleDownloadResume = (docx_url) => {
    const link = document.createElement("a");
    link.href = `${API_BASE_URL}/job/description/${docx_url}`;
    link.setAttribute("download", "resume.docx");
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  const handleDownloadPDF = async (pdf_url, filename) => {
    const response = await fetch(`${API_BASE_URL}/job/description/${pdf_url}`, {
      method: "GET",
    });

    if (!response.ok) {
      // handle error
      return;
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadCoverLetter = async (pdf_url, filename) => {
    const response = await fetch(`${API_BASE_URL}/job/description/${pdf_url}`, {
      method: "GET",
    });

    if (!response.ok) {
      return;
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleGenerateCoverLetter = async (record) => {
    if (isEmpty(record?.profile_name) && isEmpty(user?.template_coverletter_url)) {
      openNotification(
        "error",
        "Validation Error!",
        "Please upload CoverLetter Template.",
      );
      return;
    }

    if (isEmpty(record?.pdf_url)) {
      openNotification(
        "error",
        "Validation Error!",
        "You can't generate CoverLetter without Resume.",
      );
      return;
    }

    const params = {
      user: user,
      bid: record,
    };

    setLoading(true);
    await apiClient
      .post("/bids/gen-coverletter", params)
      .then(async (res) => {
        if (res.data.status === "success") {
          dispatch(updateBalance(res.data.balance));
          const downloadPDFLink = res.data.downloadPDFLink;
          const coverletter_filename = res.data.coverletter_filename;

          setSearchedData((current) =>
            current.map((item) =>
              item.id === record.id
                ? {
                    ...item,
                    coverletter_url: downloadPDFLink,
                    coverletter_filename,
                  }
                : item,
            ),
          );

          await handleDownloadCoverLetter(
            downloadPDFLink,
            coverletter_filename,
          );

          message.success(res.data.msg);
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
      });
  };

  const handleDownloadJD = async (content, companyname) => {
    const params = {
      content,
    };

    fetch(API_BASE_URL + "/api/bids/download/jd", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(params),
    })
      .then((res) => res.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${companyname}.txt`; // desired filename
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      });
  };

  const columns = [
    {
      title: "#",
      key: "no",
      render: (_, record) => record.key,
    },
    {
      title: "Service",
      key: "job_service",
      render: (_, record) => {
        const domain = getJobDomain(record?.job_url);
        return (
          <Tooltip placement="top" title={domain || "Unknown Service"}>
            <img
              className="service-icon"
              src={getJobFaviconUrl(record?.job_url)}
              alt={domain ? `${domain} icon` : "Unknown service icon"}
            />
          </Tooltip>
        );
      },
      // Retained temporarily for compatibility with any code that reads the
      // legacy column metadata; Ant Design renders the `render` function above.
      legacyServiceRender: (text, record) => (
        <Tooltip
          placement="top"
          title={
            record.job_url.toLowerCase().includes("devjob")
              ? "https://devjob.ro/en"
              : record.job_url.toLowerCase().includes("djinni")
                ? "https://djinni.co"
                : record.job_url.toLowerCase().includes("eurotechjobs")
                  ? "https://www.eurotechjobs.com/jobs/romania"
                  : record.job_url.toLowerCase().includes("glassdoor")
                    ? "https://glassdoor.ie"
                    : record.job_url.toLowerCase().includes("iamexpat")
                      ? "https://www.iamexpat.nl/career"
                      : record.job_url.toLowerCase().includes("jobright")
                        ? "https://jobright.ai"
                        : record.job_url.toLowerCase().includes("jungle")
                          ? "https://app.welcometothejungle.com"
                          : record.job_url.toLowerCase().includes("justjoin")
                            ? "https://justjoin.it"
                            : record.job_url.toLowerCase().includes("xing")
                              ? "https://xing.com"
                              : record.job_url
                                    .toLowerCase()
                                    .includes("ziprecruiter")
                                ? "https://www.ziprecruiter.com"
                                : record.job_url
                                      .toLowerCase()
                                      .includes("indeed")
                                  ? "https://indeed.com"
                                  : record.job_url
                                        .toLowerCase()
                                        .includes("dice")
                                    ? "https://www.dice.com"
                                    : record.job_url
                                          .toLowerCase()
                                          .includes("cv-library")
                                      ? "https://www.cv-library.co.uk"
                                      : record.job_url
                                            .toLowerCase()
                                            .includes("linkedin")
                                        ? "https://www.linkedin.com"
                                        : record.job_url
                                              .toLowerCase()
                                              .includes("adzuna")
                                          ? "https://www.adzuna.co.uk"
                                          : record.job_url
                                                .toLowerCase()
                                                .includes("ashbyhq")
                                            ? "https://jobs.ashbyhq.com/jobs"
                                            : record.job_url
                                                  .toLowerCase()
                                                  .includes("monster")
                                              ? "https://www.monster.co.uk"
                                              : record.job_url
                                                    .toLowerCase()
                                                    .includes("reed")
                                                ? "https://www.reed.co.uk"
                                                : record.job_url
                                                      .toLowerCase()
                                                      .includes("workable")
                                                  ? "https://jobs.workable.com"
                                                  : record.job_url
                                                        .toLowerCase()
                                                        .includes(
                                                          "euremotejobs",
                                                        )
                                                    ? "https://euremotejobs.com"
                                                    : record.job_url
                                                          .toLowerCase()
                                                          .includes(
                                                            "remoteineurope",
                                                          )
                                                      ? "https://remoteineurope.com"
                                                      : record.job_url
                                                            .toLowerCase()
                                                            .includes(
                                                              "jobserve",
                                                            )
                                                        ? "https://jobserve.com"
                                                        : record.job_url
                                                              .toLowerCase()
                                                              .includes(
                                                                "myworkdayjobs",
                                                              )
                                                          ? "https://ultra.wd3.myworkdayjobs.com"
                                                          : record.job_url
                                                                .toLowerCase()
                                                                .includes(
                                                                  "dynamitejobs",
                                                                )
                                                            ? "https://dynamitejobs.com"
                                                            : record.job_url
                                                                  .toLowerCase()
                                                                  .includes(
                                                                    "totaljobs",
                                                                  )
                                                              ? "https://www.totaljobs.com"
                                                              : record.job_url
                                                                    .toLowerCase()
                                                                    .includes(
                                                                      "danaher",
                                                                    )
                                                                ? "https://danaher.jobs"
                                                                : record.job_url
                                                                      .toLowerCase()
                                                                      .includes(
                                                                        "himalayas",
                                                                      )
                                                                  ? "https://himalayas.app"
                                                                  : record.job_url
                                                                        .toLowerCase()
                                                                        .includes(
                                                                          "nijobs",
                                                                        )
                                                                    ? "https://nijobs.com"
                                                                    : record.job_url
                                                                          .toLowerCase()
                                                                          .includes(
                                                                            "crypto-careers",
                                                                          )
                                                                      ? "https://crypto-careers.com"
                                                                      : record.job_url
                                                                            .toLowerCase()
                                                                            .includes(
                                                                              "servicenow.dejobs",
                                                                            )
                                                                        ? "https://servicenow.dejobs.org"
                                                                        : record.job_url
                                                                              .toLowerCase()
                                                                              .includes(
                                                                                "cwjobs",
                                                                              )
                                                                          ? "https://cwjobs.co.uk"
                                                                          : record.job_url
                                                                                .toLowerCase()
                                                                                .includes(
                                                                                  "lever",
                                                                                )
                                                                            ? "https://lever.co"
                                                                            : record.job_url
                                                                                  .toLowerCase()
                                                                                  .includes(
                                                                                    "dover",
                                                                                  )
                                                                              ? "https://app.dover.com"
                                                                              : record.job_url
                                                                                    .toLowerCase()
                                                                                    .includes(
                                                                                      "join.com",
                                                                                    )
                                                                                ? "https://join.com"
                                                                                : "Unknown Service"
          }
        >
          <img
            className="service-icon"
            src={
              record.job_url.toLowerCase().includes("devjob")
                ? "/assets/image/devjob.png"
                : record.job_url.toLowerCase().includes("djinni")
                  ? "/assets/image/djinni.png"
                  : record.job_url.toLowerCase().includes("eurotechjobs")
                    ? "/assets/image/eurotechjobs.png"
                    : record.job_url.toLowerCase().includes("glassdoor")
                      ? "/assets/image/glassdoor.png"
                      : record.job_url.toLowerCase().includes("iamexpat")
                        ? "/assets/image/iamexpat.png"
                        : record.job_url.toLowerCase().includes("jobright")
                          ? "/assets/image/jobright.png"
                          : record.job_url.toLowerCase().includes("jungle")
                            ? "/assets/image/jungle.png"
                            : record.job_url.toLowerCase().includes("justjoin")
                              ? "/assets/image/justjoin.png"
                              : record.job_url.toLowerCase().includes("xing")
                                ? "/assets/image/xing.png"
                                : record.job_url
                                      .toLowerCase()
                                      .includes("ziprecruiter")
                                  ? "/assets/image/ziprecruiter.png"
                                  : record.job_url
                                        .toLowerCase()
                                        .includes("indeed")
                                    ? "/assets/image/indeed.png"
                                    : record.job_url
                                          .toLowerCase()
                                          .includes("dice")
                                      ? "/assets/image/dice.png"
                                      : record.job_url
                                            .toLowerCase()
                                            .includes("cv-library")
                                        ? "/assets/image/cv-library.png"
                                        : record.job_url
                                              .toLowerCase()
                                              .includes("linkedin")
                                          ? "/assets/image/linkedin.png"
                                          : record.job_url
                                                .toLowerCase()
                                                .includes("adzuna")
                                            ? "/assets/image/adzuna.png"
                                            : record.job_url
                                                  .toLowerCase()
                                                  .includes("ashbyhq")
                                              ? "/assets/image/ashbyhq.png"
                                              : record.job_url
                                                    .toLowerCase()
                                                    .includes("monster")
                                                ? "/assets/image/monster.png"
                                                : record.job_url
                                                      .toLowerCase()
                                                      .includes("reed")
                                                  ? "/assets/image/reed.png"
                                                  : record.job_url
                                                        .toLowerCase()
                                                        .includes("workable")
                                                    ? "/assets/image/workable.png"
                                                    : record.job_url
                                                          .toLowerCase()
                                                          .includes(
                                                            "euremotejobs",
                                                          )
                                                      ? "/assets/image/euremotejobs.png"
                                                      : record.job_url
                                                            .toLowerCase()
                                                            .includes(
                                                              "remoteineurope",
                                                            )
                                                        ? "/assets/image/remoteineurope.png"
                                                        : record.job_url
                                                              .toLowerCase()
                                                              .includes(
                                                                "jobserve",
                                                              )
                                                          ? "/assets/image/jobserve.png"
                                                          : record.job_url
                                                                .toLowerCase()
                                                                .includes(
                                                                  "myworkdayjobs",
                                                                )
                                                            ? "/assets/image/myworkdayjobs.png"
                                                            : record.job_url
                                                                  .toLowerCase()
                                                                  .includes(
                                                                    "dynamitejobs",
                                                                  )
                                                              ? "/assets/image/dynamite.png"
                                                              : record.job_url
                                                                    .toLowerCase()
                                                                    .includes(
                                                                      "totaljobs",
                                                                    )
                                                                ? "/assets/image/totaljobs.png"
                                                                : record.job_url
                                                                      .toLowerCase()
                                                                      .includes(
                                                                        "danaher",
                                                                      )
                                                                  ? "/assets/image/danaher.png"
                                                                  : record.job_url
                                                                        .toLowerCase()
                                                                        .includes(
                                                                          "himalayas",
                                                                        )
                                                                    ? "/assets/image/himalayas.png"
                                                                    : record.job_url
                                                                          .toLowerCase()
                                                                          .includes(
                                                                            "nijobs",
                                                                          )
                                                                      ? "/assets/image/totaljobs.png"
                                                                      : record.job_url
                                                                            .toLowerCase()
                                                                            .includes(
                                                                              "crypto-careers",
                                                                            )
                                                                        ? "/assets/image/crypto-careers.jpg"
                                                                        : record.job_url
                                                                              .toLowerCase()
                                                                              .includes(
                                                                                "servicenow.dejobs",
                                                                              )
                                                                          ? "/assets/image/servicenow.dejobs.png"
                                                                          : record.job_url
                                                                                .toLowerCase()
                                                                                .includes(
                                                                                  "cwjobs",
                                                                                )
                                                                            ? "/assets/image/cwjobs.png"
                                                                            : record.job_url
                                                                                  .toLowerCase()
                                                                                  .includes(
                                                                                    "lever",
                                                                                  )
                                                                              ? "/assets/image/lever.png"
                                                                              : record.job_url
                                                                                    .toLowerCase()
                                                                                    .includes(
                                                                                      "dover",
                                                                                    )
                                                                                ? "/assets/image/dover.png"
                                                                                : record.job_url
                                                                                      .toLowerCase()
                                                                                      .includes(
                                                                                        "join.com",
                                                                                      )
                                                                                  ? "/assets/image/join.com.png"
                                                                                  : "/assets/image/unknown.png"
            }
            alt="job service icon"
          />
        </Tooltip>
      ),
    },
    {
      title: "Resume (.docx)",
      dataIndex: "docx_filename",
      key: "docx_filename",
      render: (text, record) => (
        <Tooltip placement="top" title="Download Docx Resume">
          {!isEmpty(text, record) ? (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadResume(record.docx_url)}
            />
          ) : (
            <Button
              disabled
              color="danger"
              variant="dashed"
              icon={<StopOutlined />}
            />
          )}
        </Tooltip>
      ),
    },
    {
      title: "Resume (.pdf)",
      dataIndex: "pdf_filename",
      key: "pdf_filename",
      render: (text, record) => (
        <Tooltip placement="top" title="Download PDF Resume">
          {!isEmpty(text, record) ? (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadPDF(record.pdf_url, text)}
            />
          ) : (
            <Button
              disabled
              color="danger"
              variant="dashed"
              icon={<StopOutlined />}
            />
          )}
        </Tooltip>
      ),
    },
    {
      title: "CoverLetter",
      dataIndex: "coverletter_filename",
      key: "coverletter_filename",
      render: (text, record) => {
        const hasResumeDocx = !isEmpty(record.docx_filename);
        return (
          <Tooltip
            placement="top"
            title={
              !hasResumeDocx
                ? "A DOCX resume is required"
                : !isEmpty(text)
                  ? "Download PDF CoverLetter"
                  : "Generate CoverLetter"
            }
          >
            {!hasResumeDocx ? (
              <Button
                disabled
                color="danger"
                variant="dashed"
                icon={<StopOutlined />}
              />
            ) : !isEmpty(text) ? (
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() =>
                  handleDownloadCoverLetter(record.coverletter_url, text)
                }
              />
            ) : (
              <Button
                color="warning"
                variant="solid"
                icon={<FormOutlined />}
                onClick={() => handleGenerateCoverLetter(record)}
              />
            )}
          </Tooltip>
        );
      },
    },
    {
      title: "Profile",
      dataIndex: "profile_name",
      key: "profile_name",
      render: (value) => (
        <Tag
          className={`application-profile-badge ${
            value ? "is-resume-profile" : "is-template-profile"
          }`}
        >
          {value || "Templates"}
        </Tag>
      ),
    },
    {
      title: "Role Title",
      dataIndex: "role_title",
      key: "role_title",
    },
    {
      title: "Salary Range",
      dataIndex: "salary_range",
      key: "salary_range",
    },
    {
      title: "Job Type",
      dataIndex: "job_type",
      key: "job_type",
    },
    {
      title: "Company Name | JD",
      dataIndex: "company_name",
      key: "company_name",
      render: (text, record) => (
        <Tooltip placement="top" title="Download JD">
          {!isEmpty(text) ? (
            <Button
              type="primary"
              onClick={() =>
                handleDownloadJD(record.job_desc, record.company_name)
              }
            >
              {text}
            </Button>
          ) : (
            <span>N/A</span>
          )}
        </Tooltip>
      ),
    },
    {
      title: "Job URL",
      dataIndex: "job_url",
      key: "job_url",
      render: (text) => (
        <Tooltip placement="top" title={text}>
          <Button
            type="primary"
            icon={<CopyOutlined />}
            onClick={() => handleCopy(text)}
          >
            Copy
          </Button>
        </Tooltip>
      ),
    },
    Table.EXPAND_COLUMN,
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text) => <Tag color={getColorFromStatus(text)}>{text}</Tag>,
    },
    {
      title: "Caller",
      key: "caller",
      dataIndex: "caller",
      render: (text, record) =>
        !isEmpty(text) ? (
          <Tag
            color={record.caller_color}
            style={{ color: record.caller_textcolor }}
          >
            {text}
          </Tag>
        ) : (
          <span>N/A</span>
        ),
    },
    {
      title: "Bid Time",
      key: "created_at",
      dataIndex: "created_at",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => {
        const hasResumeDocx = !isEmpty(record.docx_filename);
        const hasGeneratedBid = !isEmpty(record.bid);
        return (
          <div style={{ display: "flex", flexDirection: "row", gap: 10 }}>
            {canUseSpecialActions && (
              <Tooltip
                placement="top"
                title={
                  hasGeneratedBid
                    ? "Regenerate resume"
                    : "Generated bid content is unavailable"
                }
              >
                <span style={{ display: "inline-flex" }}>
                  <Button
                    disabled={!hasGeneratedBid}
                    type="primary"
                    icon={<RedoOutlined />}
                    aria-label="Regenerate resume"
                    onClick={() => navigate(`/resume-preview/${record.id}`)}
                  />
                </span>
              </Tooltip>
            )}
            {canUseSpecialActions && (
              <Tooltip
                placement="top"
                title={
                  hasResumeDocx ? "Copy details" : "A DOCX resume is required"
                }
              >
                <span style={{ display: "inline-flex" }}>
                  <Button
                    disabled={!hasResumeDocx}
                    type="default"
                    onClick={() => handleCopyDetail(record)}
                  >
                    <CopyOutlined />
                  </Button>
                </span>
              </Tooltip>
            )}
            <Tooltip
              placement="top"
              title={
                hasResumeDocx ? "Edit application" : "A DOCX resume is required"
              }
            >
              <span style={{ display: "inline-flex" }}>
                <Button
                  disabled={!hasResumeDocx}
                  type="primary"
                  onClick={() => handleClickEditBtn(record)}
                >
                  <EditOutlined />
                </Button>
              </span>
            </Tooltip>
            <Popconfirm
              title="Delete this resume"
              description="Are you sure to delete this resume?"
              okText="Yes"
              cancelText="No"
              onConfirm={() => handleItemDelete(record.id)}
            >
              <Tooltip placement="top" title="Delete application">
                <Button type="primary" danger>
                  <DeleteOutlined />
                </Button>
              </Tooltip>
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;

    // Avoid scrolling to bottom
    textArea.style.position = "fixed";
    textArea.style.top = "0";
    textArea.style.left = "0";

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      if (successful) {
        message.success("Copied to clipboard!");
      } else {
        console.warn("Fallback: Copy command was unsuccessful");
        message.error("Failed to copy!");
      }
    } catch (err) {
      console.error("Fallback: Unable to copy", err);
      message.error("Failed to copy!");
    }

    document.body.removeChild(textArea);
  };

  const handleCopy = (copyText) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(copyText)
        .then(() => {
          message.success("Copied to clipboard!");
        })
        .catch(() => {
          message.error("Failed to copy!");
        });
    } else {
      fallbackCopyTextToClipboard(copyText);
    }
  };

  const handleItemDelete = async (id) => {
    const params = {
      id,
    };

    setLoading(true);
    await apiClient
      .post("/bids/del-draft", params)
      .then(async (res) => {
        if (res.data?.status === "success") {
          openNotification("success", "Success", res.data.msg);

          await getDataAfterAction();
        }
        setLoading(false);
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
        setLoading(false);
      });
  };

  // Kept for compatibility with older callers; the Applications UI now uses
  // the paginated keyword search endpoint below.
  // eslint-disable-next-line no-unused-vars
  const handleDirectSearch = async () => {
    if (isEmpty(user)) {
      return;
    }

    const params = {
      user_id: user._id,
      search_text: directSearchText.trim(),
    };

    setLoading(true);

    await apiClient
      .post("/bids/get-bids-by-comname", params)
      .then((res) => {
        if (isEmpty(res.data?.result)) {
          setAllData([]);
          setSearchedData([]);
          setLoading(false);
        } else {
          const tempData = res.data?.result
            .map((item, key) => {
              return {
                key: key + 1,
                id: item._id,
                company_name: item.company_name,
                profile_name: item?.profile_name,
                role_title: item?.role_title,
                salary_range: item?.salary_range,
                job_type: item?.job_type,
                created_at: getDateAndTime(item.date),
                docx_filename: item.docx_filename,
                pdf_filename: item?.pdf_filename,
                job_url: item.job_url,
                job_desc: item.job_desc,
                bid: item.bid,
                docx_url: item.docx_url,
                pdf_url: item?.pdf_url,
                coverletter_filename: item?.coverletter_filename,
                coverletter_url: item?.coverletter_url,
                status:
                  item.status === 0
                    ? "Waiting"
                    : item.status === 1
                      ? "Recruiter Phone"
                      : item.status === 2
                        ? "Recruiter Video"
                        : item.status === 7
                          ? "HR Call"
                          : item.status === 3
                            ? "Technical"
                            : item.status === 4
                              ? "Behavioural"
                              : item.status === 5
                                ? "Final"
                                : "Failed",
                caller: item.caller?.name,
                caller_id: item.caller?._id,
                caller_color: item.caller?.background_color,
                caller_textcolor: item.caller?.text_color,
                meeting_link: item?.meeting_link,
                client_address: item?.client_address,
                start_date: item?.start_date,
                end_date: item?.end_date,
              };
            })
            .sort((a, b) => {
              const dataA = new Date(a.created_at);
              const dataB = new Date(b.created_at);
              return dataB - dataA;
            });

          setAllData(tempData);
          setSearchedData(tempData);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
        setLoading(false);
      });
  };

  // eslint-disable-next-line no-unused-vars
  const handleSearch = async () => {
    setSearchText("");
    setSearchStatus(0);

    await getDataAfterAction();
  };

  const getDataAfterAction = async () => {
    const originalStartTime = `${dayjs(searchStartDate).year()}-${String(
      dayjs(searchStartDate).month() + 1,
    ).padStart(2, "0")}-${String(dayjs(searchStartDate).date()).padStart(
      2,
      "0",
    )} 00:00:00`;

    const timeStartInUTC = dayjs(originalStartTime).tz(
      timezoneMode.timezoneMode,
      true,
    );
    const timeStartInGMTPlus9 = timeStartInUTC.tz(
      timezoneMode.timezoneMode,
      true,
    );

    const originalEndTime = `${dayjs(searchEndDate).year()}-${String(
      dayjs(searchEndDate).month() + 1,
    ).padStart(2, "0")}-${String(dayjs(searchEndDate).date()).padStart(
      2,
      "0",
    )} 23:59:59`;
    const timeEndInUTC = dayjs(originalEndTime).tz(
      timezoneMode.timezoneMode,
      true,
    );
    const timeEndInGMTPlus9 = timeEndInUTC.tz(timezoneMode.timezoneMode, true);

    const params = {
      user_id: user._id,
      searchStartDate: appliedSearchAllApplications
        ? undefined
        : timeStartInGMTPlus9,
      searchEndDate: appliedSearchAllApplications
        ? undefined
        : timeEndInGMTPlus9,
      keyword: debouncedSearchText,
      status: appliedSearchAllApplications ? 0 : searchStatus,
      page: currentPage,
      pageSize,
    };

    setLoading(true);

    await apiClient
      .post("/bids/get", params)
      .then((res) => {
        setTotalResults(res.data?.pagination?.total || 0);
        // set statistic value
        if (!isEmpty(res.data?.allResult)) {
          setTotalBid(res.data?.allResult.totalBid);
          setInProgressBid(res.data?.allResult.inProgressBid);
          setWaitingBid(res.data?.allResult.waitingBid);
          setFailedBid(res.data?.allResult.failedBid);
        }

        if (isEmpty(res.data?.result)) {
          setAllData([]);
          setSearchedData([]);
          setLoading(false);
        } else {
          const tempData = res.data?.result
            .map((item, key) => {
              return {
                key: (currentPage - 1) * pageSize + key + 1,
                id: item._id,
                company_name: item.company_name,
                profile_name: item?.profile_name,
                role_title: item?.role_title,
                salary_range: item?.salary_range,
                job_type: item?.job_type,
                created_at: getDateAndTime(item.date),
                docx_filename: item.docx_filename,
                pdf_filename: item?.pdf_filename,
                job_url: item.job_url,
                job_desc: item.job_desc,
                bid: item.bid,
                docx_url: item.docx_url,
                pdf_url: item?.pdf_url,
                coverletter_filename: item?.coverletter_filename,
                coverletter_url: item?.coverletter_url,
                status:
                  item.status === 0
                    ? "Waiting"
                    : item.status === 1
                      ? "Recruiter Phone"
                      : item.status === 2
                        ? "Recruiter Video"
                        : item.status === 7
                          ? "HR Call"
                          : item.status === 3
                            ? "Technical"
                            : item.status === 4
                              ? "Behavioural"
                              : item.status === 5
                                ? "Final"
                                : "Failed",
                caller: item.caller?.name,
                caller_id: item.caller?._id,
                caller_color: item.caller?.background_color,
                caller_textcolor: item.caller?.text_color,
                meeting_link: item?.meeting_link,
                client_address: item?.client_address,
                start_date: item?.start_date,
                end_date: item?.end_date,
              };
            })
            .sort((a, b) => {
              const dataA = new Date(a.created_at);
              const dataB = new Date(b.created_at);
              return dataB - dataA;
            });

          setAllData(tempData);
          setSearchedData(tempData);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
        setLoading(false);
      });
  };

  // get bids data
  const getData = useCallback(async () => {
    const originalStartTime = `${dayjs(searchStartDate).year()}-${String(
      dayjs(searchStartDate).month() + 1,
    ).padStart(2, "0")}-${String(dayjs(searchStartDate).date()).padStart(
      2,
      "0",
    )} 00:00:00`;

    const timeStartInUTC = dayjs(originalStartTime).tz(
      timezoneMode.timezoneMode,
      true,
    );
    const timeStartInGMTPlus9 = timeStartInUTC.tz(
      timezoneMode.timezoneMode,
      true,
    );

    const originalEndTime = `${dayjs(searchEndDate).year()}-${String(
      dayjs(searchEndDate).month() + 1,
    ).padStart(2, "0")}-${String(dayjs(searchEndDate).date()).padStart(
      2,
      "0",
    )} 23:59:59`;
    const timeEndInUTC = dayjs(originalEndTime).tz(
      timezoneMode.timezoneMode,
      true,
    );
    const timeEndInGMTPlus9 = timeEndInUTC.tz(timezoneMode.timezoneMode, true);

    const params = {
      user_id: user._id,
      searchStartDate: appliedSearchAllApplications
        ? undefined
        : timeStartInGMTPlus9,
      searchEndDate: appliedSearchAllApplications
        ? undefined
        : timeEndInGMTPlus9,
      keyword: debouncedSearchText,
      status: appliedSearchAllApplications ? 0 : searchStatus,
      page: currentPage,
      pageSize,
    };

    setLoading(true);

    await apiClient
      .post("/bids/get", params)
      .then((res) => {
        setTotalResults(res.data?.pagination?.total || 0);
        // set statistic value
        if (!isEmpty(res.data?.allResult)) {
          setTotalBid(res.data?.allResult.totalBid);
          setInProgressBid(res.data?.allResult.inProgressBid);
          setWaitingBid(res.data?.allResult.waitingBid);
          setFailedBid(res.data?.allResult.failedBid);
        }

        if (isEmpty(res.data?.result)) {
          setAllData([]);
          setSearchedData([]);
          setLoading(false);
        } else {
          const tempData = res.data?.result
            .map((item, key) => {
              return {
                key: (currentPage - 1) * pageSize + key + 1,
                id: item._id,
                company_name: item.company_name,
                profile_name: item?.profile_name,
                role_title: item?.role_title,
                salary_range: item?.salary_range,
                job_type: item?.job_type,
                created_at: getDateAndTime(item.date),
                docx_filename: item.docx_filename,
                pdf_filename: item?.pdf_filename,
                job_url: item.job_url,
                job_desc: item.job_desc,
                bid: item.bid,
                docx_url: item.docx_url,
                pdf_url: item?.pdf_url,
                coverletter_filename: item?.coverletter_filename,
                coverletter_url: item?.coverletter_url,
                status:
                  item.status === 0
                    ? "Waiting"
                    : item.status === 1
                      ? "Recruiter Phone"
                      : item.status === 2
                        ? "Recruiter Video"
                        : item.status === 7
                          ? "HR Call"
                          : item.status === 3
                            ? "Technical"
                            : item.status === 4
                              ? "Behavioural"
                              : item.status === 5
                                ? "Final"
                                : "Failed",
                caller: item.caller?.name,
                caller_id: item.caller?._id,
                caller_color: item.caller?.background_color,
                caller_textcolor: item.caller?.text_color,
                meeting_link: item?.meeting_link,
                client_address: item?.client_address,
                start_date: item?.start_date,
                end_date: item?.end_date,
              };
            })
            .sort((a, b) => {
              const dataA = new Date(a.created_at);
              const dataB = new Date(b.created_at);
              return dataB - dataA;
            });

          setAllData(tempData);
          setSearchedData(tempData);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
        setLoading(false);
      });
  }, [
    user,
    searchStartDate,
    searchEndDate,
    debouncedSearchText,
    searchStatus,
    appliedSearchAllApplications,
    currentPage,
    pageSize,
    timezoneMode.timezoneMode,
  ]);

  const getUsers = async () => {
    await apiClient
      .get("/users/scope-users")
      .then((res) => {
        if (isEmpty(res.data)) {
          setModelUsers([]);
        } else {
          const tempUsers = res.data.map((item, key) => {
            return {
              key,
              value: item._id,
              label: item.name,
            };
          });
          setModelUsers(tempUsers);
        }
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
      });
  };

  useEffect(() => {
    if (user && !isEmpty(user._id)) {
      getUsers();
      getData();
    }
  }, [user, getData]);

  return (
    <div
      className={`dashboardSection ${themeMode.themeMode ? "dashboard-dark" : ""}`}
    >
      <div className="dashboard-heading">
        <div>
          <h2 className="title">
            <span className="title-icon">
              <PieChartOutlined />
            </span>
            Applications
          </h2>
          <p>Track applications and manage your recruitment pipeline.</p>
        </div>
        <Tag className="result-badge">{totalResults} results</Tag>
      </div>
      <div className="statistic-section">
        <Row gutter={[18, 18]}>
          <Col xs={24} sm={12} xl={6}>
            <Card variant="borderless" className="stat-card stat-card-total">
              <Statistic
                title="Total Bids"
                value={totalBid}
                precision={0}
                valueStyle={{ color: themeMode.themeMode ? "#fff" : "#000" }}
                prefix={<BarChartOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Card variant="borderless" className="stat-card stat-card-progress">
              <Statistic
                title="In Progress"
                value={inProgressBid}
                precision={0}
                valueStyle={{ color: "#3f8600" }}
                prefix={<ArrowUpOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Card variant="borderless" className="stat-card stat-card-waiting">
              <Statistic
                title="Waiting"
                value={waitingBid}
                precision={0}
                valueStyle={{ color: "#e1cc12" }}
                prefix={<EyeOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Card variant="borderless" className="stat-card stat-card-failed">
              <Statistic
                title="Failed"
                value={failedBid}
                precision={0}
                valueStyle={{ color: "#cf1322" }}
                prefix={<BugOutlined />}
              />
            </Card>
          </Col>
        </Row>
      </div>
      <Card className="search-section" variant="borderless">
        <div className="filter-heading">
          <div>
            <SearchOutlined /> Search & filters
          </div>
          <span>Refine your application list</span>
        </div>
        <div className="filter-grid">
          <div className="field">
            <div className="label">Keywords</div>
            <Input
              prefix={<SearchOutlined className="input-icon" />}
              placeholder="e.g. Frontend Engineer"
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={() => {
                setCurrentPage(1);
                setDebouncedSearchText(searchText.trim());
                setAppliedSearchAllApplications(searchAllApplications);
              }}
              value={searchText}
            />
          </div>
          <div className="field status-filter-field">
            <div className="label">Status</div>
            <Select
              value={searchStatus}
              disabled={searchAllApplications}
              onChange={(value) => {
                setCurrentPage(1);
                setSearchStatus(value);
              }}
              options={[
                {
                  value: 0,
                  label: "All",
                },
                {
                  value: 1,
                  label: "Waiting",
                },
                {
                  value: 2,
                  label: "In Progress",
                },
                {
                  value: 3,
                  label: "Failed",
                },
              ]}
            />
          </div>
          <div className="field">
            <div className="label">Date range</div>
            <RangePicker
              value={[searchStartDate, searchEndDate]}
              format={dateFormat}
              disabled={loading || searchAllApplications}
              onChange={(dates, dateStrings) => {
                if (dates) {
                  setCurrentPage(1);
                  setSearchStartDate(
                    dayjs(dateStrings[0]).tz(timezoneMode.timezoneMode, true),
                  );
                  setSearchEndDate(
                    dayjs(dateStrings[1]).tz(timezoneMode.timezoneMode, true),
                  );
                } else {
                  setSearchStartDate(
                    dayjs(getDate(dayjs())).tz(timezoneMode.timezoneMode, true),
                  );
                  setSearchEndDate(
                    dayjs(getDate(dayjs())).tz(timezoneMode.timezoneMode, true),
                  );
                }
              }}
            />
          </div>
          <div className="field applications-all-filter">
            <div className="label">Search scope</div>
            <Checkbox
              checked={searchAllApplications}
              onChange={(event) => {
                setSearchAllApplications(event.target.checked);
              }}
            >
              Search all applications
            </Checkbox>
          </div>
        </div>
      </Card>
      <Card className="table-section" variant="borderless">
        <div className="table-heading">
          <div>
            <h3>Applications</h3>
            <span>Your latest job activity</span>
          </div>
          <Tag>{totalResults} records</Tag>
        </div>
        <Table
          columns={columns}
          dataSource={searchedData}
          loading={loading}
          size="middle"
          scroll={{ x: 1100 }}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalResults,
            showSizeChanger: true,
            showTotal: (total) => `${total} applications`,
          }}
          onChange={(pagination) => {
            setCurrentPage(pagination.current);
            setPageSize(pagination.pageSize);
          }}
          expandable={{
            expandedRowRender: (record) => (
              <div className="call-subsection">
                <div className="meeting-link">
                  <a
                    href={record.meeting_link}
                    target="_blank"
                    rel="noreferrer"
                    className="link-a"
                  >
                    {record.meeting_link}
                  </a>
                </div>
                <div className="client-address">{record.client_address}</div>
                <div className="meeting-date">
                  {getDateAndTime(record.start_date)} ~{" "}
                  {getDateAndTime(record.end_date)}
                </div>
              </div>
            ),
            rowExpandable: (record) =>
              record.status !== "Waiting" && record.status !== "Failed",
          }}
        />
        <Modal
          title="Edit application"
          open={isCallerModalOpen}
          onOk={handleModalSave}
          onCancel={handleModalCancel}
          centered
          className="dashboard-edit-modal"
          footer={[
            <Button
              key="submit"
              type="primary"
              loading={editModalLoading}
              onClick={handleModalSave}
              icon={<SaveOutlined />}
            >
              Save changes
            </Button>,
          ]}
        >
          <div className="modal-field">
            <label className="label">Resume Docx Name</label>
            <Input
              value={editFileName}
              onChange={(e) => setEditFileName(e.target.value)}
            />
          </div>
          <div className="modal-field">
            <label className="label">Company Name</label>
            <Input
              value={editCompanyName}
              onChange={(e) => setEditCompanyName(e.target.value)}
            />
          </div>
          <div className="modal-field">
            <label className="label">Status</label>
            <Select
              value={editStatusText}
              onChange={(value) => {
                setCallDataFlag(
                  value === 0 ? false : value === 6 ? false : true,
                );
                setEditStatusText(value);
              }}
              style={{ width: "100%" }}
              options={[
                {
                  value: 0,
                  label: "Waiting",
                },
                {
                  value: 1,
                  label: "Recruiter Phone",
                },
                {
                  value: 2,
                  label: "Recruiter Video",
                },
                {
                  value: 7,
                  label: "HR Call",
                },
                {
                  value: 3,
                  label: "Technical",
                },
                {
                  value: 4,
                  label: "Behavioural",
                },
                {
                  value: 5,
                  label: "Final",
                },
                {
                  value: 6,
                  label: "Failed",
                },
              ]}
            />
          </div>
          <div className="modal-field">
            <label className="label">Caller</label>
            <Select
              value={editCallerName}
              onChange={(value) => setEditCallerName(value)}
              style={{ width: "100%" }}
              options={[
                {
                  value: "",
                  label: "Please choose caller",
                },
                ...modelUsers,
              ]}
            />
          </div>
          {callDataFlag ? (
            <>
              <div className="modal-field">
                <label className="label">Meeting Link</label>
                <Input
                  placeholder="i.e, https://meet.google.com/oxj-rhwy-ino"
                  value={editMeetingLink}
                  onChange={(e) => setEditMeetingLink(e.target.value)}
                />
              </div>
              <div className="modal-field">
                <label className="label">Client Address</label>
                <Input
                  placeholder="i.e, emilyhood95@hotmail.com"
                  value={editClientAddress}
                  onChange={(e) => setEditClientAddress(e.target.value)}
                />
              </div>
              <div className="modal-field">
                <label className="label">Meeting Time</label>
                <div className="meeting-time-fields">
                  <DatePicker
                    showTime
                    format={dateAndTimeFormat}
                    placeholder="Start Date"
                    value={editStartDate}
                    onChange={(value, dateString) => {
                      const originalTime = dateString;
                      const timeInUTC = dayjs(originalTime).tz(
                        timezoneMode.timezoneMode,
                        true,
                      );
                      setEditStartDate(timeInUTC);
                    }}
                  />
                  <DatePicker
                    showTime
                    format={dateAndTimeFormat}
                    placeholder="End Date"
                    value={editEndDate}
                    onChange={(value, dateString) => {
                      const originalTime = dateString;
                      const timeInUTC = dayjs(originalTime).tz(
                        timezoneMode.timezoneMode,
                        true,
                      );
                      setEditEndDate(timeInUTC);
                    }}
                  />
                </div>
              </div>
            </>
          ) : (
            <></>
          )}
        </Modal>
      </Card>
    </div>
  );
};

export default Dashboard;
